<template>
  <section class="stock-take-page">
    <div class="page-header">
      <div>
        <h1>Stock-Take</h1>
        <p class="page-copy">
          Saved Stocky inventory reports with free stock, bundle stock, and total stock quantities.
        </p>
      </div>

      <div class="page-actions">
        <button class="btn btn-primary" type="button" :disabled="generating || loading" @click="generateManualReport">
          {{ generating ? 'Generating...' : 'Generate Manual Report' }}
        </button>
        <button class="btn btn-outline-primary" type="button" :disabled="loading || generating" @click="loadReports">
          {{ loading ? 'Loading...' : 'Refresh' }}
        </button>
      </div>
    </div>

    <div v-if="error" class="alert alert-danger">
      {{ error }}
    </div>

    <div v-if="infoMessage" class="alert alert-info">
      {{ infoMessage }}
    </div>

    <div class="meta-grid">
      <div class="meta-card">
        <span class="meta-label">Reports</span>
        <strong>{{ reports.length }}</strong>
      </div>
      <div class="meta-card">
        <span class="meta-label">Ready</span>
        <strong>{{ readyCount }}</strong>
      </div>
      <div class="meta-card">
        <span class="meta-label">Failed</span>
        <strong>{{ failedCount }}</strong>
      </div>
    </div>

    <div class="reports-panel">
      <div class="reports-panel-header">
        <div>
          <h2>Saved Reports</h2>
          <p class="query-meta">
            Monthly auto snapshots and manual snapshots are stored as XLSX files with metadata.
          </p>
        </div>
      </div>

      <div v-if="loading" class="loading-state">
        Loading saved Stock-Take reports...
      </div>

      <div v-else-if="reports.length === 0" class="empty-state">
        No Stock-Take reports have been generated yet.
      </div>

      <div v-else class="table-container">
        <table class="table table-sm table-hover align-middle">
          <thead>
            <tr>
              <th>Report</th>
              <th>Type</th>
              <th>Snapshot Date</th>
              <th>Status</th>
              <th>Generated</th>
              <th>Rows</th>
              <th>Attempts</th>
              <th>Email</th>
              <th>Error</th>
              <th class="actions-column">Download</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="report in reports" :key="report.id">
              <td>
                <strong>{{ report.file_name || report.id }}</strong>
                <div class="table-subtext">{{ report.id }}</div>
              </td>
              <td>{{ typeLabel(report.type) }}</td>
              <td>{{ report.snapshot_date || '-' }}</td>
              <td>
                <span class="status-pill" :class="statusClass(report.status)">
                  {{ statusLabel(report.status) }}
                </span>
                <div v-if="report.next_retry_after" class="table-subtext">
                  Retry after {{ formatTimestamp(report.next_retry_after) }}
                </div>
              </td>
              <td>{{ formatTimestamp(report.generated_at || report.last_attempt_at) }}</td>
              <td>{{ report.row_count ?? 0 }}</td>
              <td>{{ report.attempt_count ?? 0 }} / {{ report.max_attempts ?? 1 }}</td>
              <td>
                <span v-if="report.email" class="status-pill" :class="emailStatusClass(report.email.last_status)">
                  {{ emailStatusLabel(report.email.last_status) }}
                </span>
                <span v-else>-</span>
                <button
                  v-if="report.email?.last_error"
                  class="detail-link table-warning"
                  type="button"
                  @click="showDetail('Email Error', report.email.last_error)"
                >
                  {{ truncateText(report.email.last_error) }}
                </button>
              </td>
              <td>
                <button
                  v-if="report.error"
                  class="detail-link table-warning"
                  type="button"
                  @click="showDetail('Report Error', report.error)"
                >
                  {{ truncateText(report.error) }}
                </button>
                <span v-else>-</span>
              </td>
              <td class="actions-column">
                <button class="btn btn-sm btn-outline-dark" type="button" :disabled="report.status !== 'ready' || busyReportId === report.id" @click="downloadReport(report)">
                  {{ busyReportId === report.id ? 'Downloading...' : 'Download' }}
                </button>
                <div v-if="report.file_size" class="table-subtext">{{ formatFileSize(report.file_size) }}</div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-if="detailModal" class="detail-modal-backdrop" role="presentation" @click.self="closeDetail">
      <div class="detail-modal" role="dialog" aria-modal="true" :aria-label="detailModal.title">
        <div class="detail-modal-header">
          <h2>{{ detailModal.title }}</h2>
          <button class="btn-close" type="button" aria-label="Close" @click="closeDetail"></button>
        </div>
        <pre>{{ detailModal.message }}</pre>
      </div>
    </div>
  </section>
</template>

<script>
import api from '@/utils/api'

export default {
  name: 'StockTakeView',
  data() {
    return {
      reports: [],
      loading: false,
      generating: false,
      busyReportId: '',
      error: null,
      infoMessage: '',
      detailModal: null
    }
  },
  computed: {
    readyCount() {
      return this.reports.filter((report) => report.status === 'ready').length
    },
    failedCount() {
      return this.reports.filter((report) => report.status === 'failed').length
    }
  },
  async mounted() {
    await this.loadReports()
  },
  methods: {
    async loadReports() {
      this.loading = true
      this.error = null

      try {
        const response = await api.get('/api/endpoints/stock-take-reports.php')
        if (!response.data?.success) {
          throw new Error(response.data?.error || 'Failed to load Stock-Take reports.')
        }

        this.reports = Array.isArray(response.data.data?.reports) ? response.data.data.reports : []
      } catch (error) {
        this.reports = []
        this.error = error?.response?.data?.error || error.message || 'Failed to load Stock-Take reports.'
      } finally {
        this.loading = false
      }
    },
    async generateManualReport() {
      this.generating = true
      this.error = null
      this.infoMessage = ''

      try {
        const response = await api.post('/api/endpoints/stock-take-generate.php')
        if (!response.data?.success) {
          throw new Error(response.data?.error || 'Failed to generate Stock-Take report.')
        }

        this.reports = Array.isArray(response.data.data?.reports) ? response.data.data.reports : []
        const report = response.data.data?.report || null
        this.infoMessage = report?.file_name
          ? `Generated ${report.file_name}.`
          : 'Stock-Take report generated.'
      } catch (error) {
        this.error = error?.response?.data?.error || error.message || 'Failed to generate Stock-Take report.'
      } finally {
        this.generating = false
      }
    },
    async downloadReport(report) {
      const reportId = report?.id
      if (!reportId) {
        return
      }

      this.busyReportId = reportId
      this.error = null

      try {
        const response = await api.get('/api/endpoints/stock-take-download.php', {
          params: { id: reportId },
          responseType: 'blob'
        })

        const blobUrl = URL.createObjectURL(response.data)
        const disposition = response.headers['content-disposition'] || ''
        const matchedFileName = disposition.match(/filename="?([^";]+)"?/i)
        const fileName = matchedFileName?.[1] || report.file_name || `${reportId}.xlsx`
        const link = document.createElement('a')
        link.href = blobUrl
        link.download = fileName
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(blobUrl)
      } catch (error) {
        this.error = error?.response?.data?.error || error.message || 'Failed to download Stock-Take report.'
      } finally {
        this.busyReportId = ''
      }
    },
    typeLabel(type) {
      return {
        auto: 'Auto',
        manual: 'Manual'
      }[type] || type || '-'
    },
    statusLabel(status) {
      return {
        missing: 'Missing',
        running: 'Running',
        ready: 'Ready',
        failed: 'Failed'
      }[status] || status || '-'
    },
    statusClass(status) {
      return {
        missing: 'status-missing',
        running: 'status-running',
        ready: 'status-ready',
        failed: 'status-failed'
      }[status] || ''
    },
    emailStatusLabel(status) {
      return {
        not_sent: 'Not Sent',
        sent: 'Sent',
        failed: 'Failed'
      }[status] || status || '-'
    },
    emailStatusClass(status) {
      return {
        not_sent: 'status-not-sent',
        sent: 'status-ready',
        failed: 'status-failed'
      }[status] || ''
    },
    formatTimestamp(value) {
      if (!value) {
        return '-'
      }

      return String(value).replace('T', ' ').replace('+02:00', '').replace('+01:00', '').replace('+00:00', '')
    },
    formatFileSize(value) {
      const bytes = Number(value || 0)
      if (!bytes) {
        return '-'
      }

      if (bytes < 1024) {
        return `${bytes} B`
      }

      if (bytes < 1024 * 1024) {
        return `${(bytes / 1024).toFixed(1)} KB`
      }

      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    },
    truncateText(value, maxLength = 90) {
      const text = String(value || '').replace(/\s+/g, ' ').trim()
      if (text.length <= maxLength) {
        return text
      }

      return `${text.slice(0, maxLength - 1)}...`
    },
    showDetail(title, message) {
      this.detailModal = {
        title,
        message: String(message || '')
      }
    },
    closeDetail() {
      this.detailModal = null
    }
  }
}
</script>

<style scoped>
.stock-take-page {
  max-width: 1600px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  gap: 1.5rem;
  align-items: flex-start;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
}

.page-header h1 {
  margin-bottom: 0.35rem;
}

.page-copy {
  margin: 0;
  color: #475569;
  max-width: 46rem;
}

.page-actions {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.meta-card,
.reports-panel,
.empty-state,
.loading-state {
  border: 1px solid #dbe3ef;
  border-radius: 1rem;
  background: #ffffff;
  box-shadow: 0 0.75rem 2rem rgba(15, 23, 42, 0.05);
}

.meta-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.meta-card {
  padding: 1rem 1.15rem;
}

.meta-label {
  display: block;
  margin-bottom: 0.35rem;
  color: #64748b;
  font-size: 0.82rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.reports-panel {
  padding: 1.25rem;
}

.reports-panel-header {
  margin-bottom: 1rem;
}

.reports-panel-header h2 {
  margin: 0 0 0.25rem;
  font-size: 1.15rem;
}

.query-meta {
  margin: 0;
  color: #64748b;
}

.loading-state,
.empty-state {
  padding: 1.5rem;
}

.table-container {
  overflow: auto;
  max-height: 620px;
}

.table {
  margin-bottom: 0;
  min-width: max-content;
}

.table th {
  white-space: nowrap;
}

.table td {
  vertical-align: top;
}

.table-subtext {
  color: #64748b;
  font-size: 0.85rem;
  margin-top: 0.2rem;
}

.table-warning {
  color: #b45309;
  font-size: 0.85rem;
}

.detail-link {
  display: block;
  max-width: 18rem;
  margin-top: 0.25rem;
  padding: 0;
  border: 0;
  background: transparent;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
}

.detail-link:hover,
.detail-link:focus {
  text-decoration: underline;
}

.detail-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(15, 23, 42, 0.45);
}

.detail-modal {
  width: min(720px, 100%);
  max-height: min(720px, calc(100vh - 2rem));
  overflow: hidden;
  border-radius: 0.5rem;
  background: #ffffff;
  box-shadow: 0 1.5rem 4rem rgba(15, 23, 42, 0.25);
}

.detail-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #e2e8f0;
}

.detail-modal-header h2 {
  margin: 0;
  font-size: 1rem;
}

.detail-modal pre {
  max-height: calc(100vh - 8rem);
  margin: 0;
  padding: 1rem 1.25rem;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
  color: #334155;
  font-size: 0.9rem;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.65rem;
  border-radius: 999px;
  font-size: 0.82rem;
  font-weight: 600;
  white-space: nowrap;
}

.status-ready {
  background: #dcfce7;
  color: #166534;
}

.status-missing,
.status-running,
.status-not-sent {
  background: #fef3c7;
  color: #92400e;
}

.status-failed {
  background: #fee2e2;
  color: #991b1b;
}

.actions-column {
  white-space: nowrap;
}

@media (max-width: 768px) {
  .page-actions {
    width: 100%;
  }

  .page-actions .btn {
    flex: 1 1 auto;
  }
}
</style>
